import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { Calendar, Plus, X } from "react-feather";
import {
  Collapse,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Loader,
  Select as DsSelect,
  TextInput,
} from "../../../../ui";
import { SegmentedControl } from "./ui-pending";
import Chip from "./ui-pending/Chip";
import Switch from "./ui-pending/Switch";
import "./slots.scss";
import {
  createAeUnavailability,
  createTeacherScheduleTemplates,
  deleteAeUnavailabilityById,
  deleteTeacherScheduleTemplates,
  getAeUnavailability,
  getTeacherScheduleTemplates,
  updateTeacherScheduleTemplates,
  type CreateTeacherScheduleTemplateBody,
  type AeUnavailabilitySlotResponse,
  type DeleteTeacherScheduleTemplatesOptions,
  type TeacherScheduleTemplatesResponse,
  type UpdateTeacherScheduleTemplateBody,
} from "../../../../Api/apiUtils";
import { ConfigContext } from "../../../../Api/global/ApiContext";
import { toast } from "react-toastify";
import { MixpanelEvents, trackEvent } from "../../../../utils/mixpanelTracking";

interface SlotsProps {
  teacherId: string;
  teacherProfileData:any;
}

interface Slot {
  id: string;
  startTime: string;
  endTime: string;
  specialSlot: boolean;
}
interface LeaveDateConfig {
  id: string;
  date: string;
  dayKey: string;
  type: "CUSTOM_SLOTS" | "FULL_DAY_LEAVE" | "REMOVE_SLOTS";
  slots: Slot[];
}

const DAYS = [
  { key: "MONDAY", label: "Monday", short: "Mon" },
  { key: "TUESDAY", label: "Tuesday", short: "Tue" },
  { key: "WEDNESDAY", label: "Wednesday", short: "Wed" },
  { key: "THURSDAY", label: "Thursday", short: "Thu" },
  { key: "FRIDAY", label: "Friday", short: "Fri" },
  { key: "SATURDAY", label: "Saturday", short: "Sat" },
  { key: "SUNDAY", label: "Sunday", short: "Sun" },
];

const toApiTime = (hhmm: string) => `${hhmm}:00`;
const toAmPm = (hhmm: string) => {
  if (!hhmm || typeof hhmm !== "string") return "—";
  const parts = hhmm.split(":");
  const h = Number(parts[0]);
  const m = Number(parts[1]);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return "—";
  const hour12 = h % 12 || 12;
  const suffix = h >= 12 ? "PM" : "AM";
  return `${hour12}:${String(m).padStart(2, "0")} ${suffix}`;
};
const TIME_OPTIONS_30 = Array.from({ length: 48 }, (_, index) => {
  const hour = String(Math.floor(index / 2)).padStart(2, "0");
  const minute = index % 2 === 0 ? "00" : "30";
  const value = `${hour}:${minute}`;
  return { value, label: toAmPm(value) };
});

const TIME_OPTIONS_60 = Array.from({ length: 24 }, (_, hourIdx) => {
  const value = `${String(hourIdx).padStart(2, "0")}:00`;
  return { value, label: toAmPm(value) };
});
const getDayKeyFromDate = (date: string) => {
  const dayIndex = new Date(`${date}T00:00:00`).getDay(); // 0 Sunday
  const mapped = (dayIndex + 6) % 7; // 0 Monday
  return DAYS[mapped]?.key || "MONDAY";
};
const getDayLabel = (dayKey: string) => DAYS.find((d) => d.key === dayKey)?.label || dayKey;

/** YYYY-MM-DD for PUT `effectiveFrom` (matches existing reset/payload patterns in this module). */
const getTodayDateString = () => new Date().toISOString().split("T")[0];

const isPastDateString = (dateStr: string) => {
  const trimmed = dateStr.trim();
  if (!trimmed) return false;
  return trimmed < getTodayDateString();
};

const handleFutureDateInput = (value: string, setter: (next: string) => void) => {
  if (value && isPastDateString(value)) {
    toast.error("Past dates are not allowed.");
    return;
  }
  setter(value);
};

const timeToMinutes = (hhmm: string) => {
  const [h, m] = hhmm.split(":").map(Number);
  return (Number.isFinite(h) ? h : 0) * 60 + (Number.isFinite(m) ? m : 0);
};

/** Map minutes to HH:mm on a 24-hour clock (1440 → 00:00). */
const minutesToTime = (mins: number) => {
  const normalized = ((mins % (24 * 60)) + 24 * 60) % (24 * 60);
  const h = Math.floor(normalized / 60);
  const m = normalized % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

/** When end is at or before start on the clock, treat end as the next calendar day (e.g. 23:00 → 00:00). */
const resolveScheduleRangeEndMinutes = (startHHMM: string, endHHMM: string) => {
  const startMin = timeToMinutes(startHHMM);
  let endMin = timeToMinutes(endHHMM);
  if (endMin <= startMin) endMin += 24 * 60;
  return { startMin, endMin };
};

const isOvernightScheduleEndTime = (startHHMM: string, endHHMM: string) =>
  timeToMinutes(endHHMM) <= timeToMinutes(startHHMM);

/**
 * Fixed-size blocks from start through end (e.g. 60 min: 13:00–18:00 → five 1h slots;
 * 30 min: 13:00–14:00 → 13:00–13:30 and 13:30–14:00). Partial tail shorter than one step is omitted.
 */
const splitRangeIntoFixedSegments = (
  startHHMM: string,
  endHHMM: string,
  stepMinutes: 30 | 60
): { segments: { startTime: string; endTime: string }[]; hasPartialTail: boolean } => {
  const { startMin, endMin } = resolveScheduleRangeEndMinutes(startHHMM, endHHMM);
  let cur = startMin;
  const segments: { startTime: string; endTime: string }[] = [];
  while (cur + stepMinutes <= endMin) {
    segments.push({ startTime: minutesToTime(cur), endTime: minutesToTime(cur + stepMinutes) });
    cur += stepMinutes;
  }
  const hasPartialTail = cur < endMin;
  return { segments, hasPartialTail };
};

const sortSlots = (slots: Slot[]) =>
  [...slots].sort((a, b) => {
    if (a.startTime === b.startTime) return a.endTime.localeCompare(b.endTime);
    return a.startTime.localeCompare(b.startTime);
  });
const sortByTime = <T extends { startTime: string; endTime: string }>(slots: T[]) =>
  [...slots].sort((a, b) => {
    if (a.startTime === b.startTime) return a.endTime.localeCompare(b.endTime);
    return a.startTime.localeCompare(b.startTime);
  });

const createEmptySlotsByDay = (): Record<string, Slot[]> => ({
  MONDAY: [],
  TUESDAY: [],
  WEDNESDAY: [],
  THURSDAY: [],
  FRIDAY: [],
  SATURDAY: [],
  SUNDAY: [],
});

const normalizeTemplateTime = (t: string | null | undefined): string => {
  if (t == null || typeof t !== "string") return "";
  const trimmed = t.trim();
  if (!trimmed) return "";
  const parts = trimmed.split(":");
  if (parts.length >= 2) {
    return `${parts[0].padStart(2, "0")}:${parts[1].padStart(2, "0")}`;
  }
  return trimmed;
};

const mapScheduleTemplatesToSlotsByDay = (data: {
  days?: Array<{ day: string; timings?: Array<{ id: number; startTime: string; endTime: string }> }>;
} | null): Record<string, Slot[]> => {
  const next = createEmptySlotsByDay();
  if (!data?.days?.length) return next;
  const validDayKeys = new Set(DAYS.map((d) => d.key));
  for (const dayBlock of data.days) {
    const key = dayBlock.day;
    if (!validDayKeys.has(key)) continue;
    next[key] = sortByTime(dayBlock.timings || [])
      .map((row) => ({
        id: String(row.id),
        startTime: normalizeTemplateTime(row.startTime),
        endTime: normalizeTemplateTime(row.endTime),
        specialSlot: false,
      }))
      .filter((slot) => slot.startTime && slot.endTime);
  }
  return next;
};

/** Merge PUT response (only processed days) into existing week state without dropping other days. */
const mergeSchedulePutDaysIntoSlots = (
  prevSlots: Record<string, Slot[]>,
  data: TeacherScheduleTemplatesResponse
): Record<string, Slot[]> => {
  const next = { ...prevSlots };
  const validDayKeys = new Set(DAYS.map((d) => d.key));
  for (const dayBlock of data.days || []) {
    const key = dayBlock.day;
    if (!validDayKeys.has(key)) continue;
    next[key] = sortByTime(dayBlock.timings || [])
      .map((row) => ({
        id: String(row.id),
        startTime: normalizeTemplateTime(row.startTime),
        endTime: normalizeTemplateTime(row.endTime),
        specialSlot: false,
      }))
      .filter((slot) => slot.startTime && slot.endTime);
  }
  return next;
};

/** True when GET returned at least one non-empty day (use POST to create; otherwise PUT to diff-update). */
const scheduleResponseHasPersistedTemplates = (data: TeacherScheduleTemplatesResponse): boolean =>
  data.days?.some((d) => (d.timings?.length ?? 0) > 0) ?? false;

type ScheduleFormSnapshot = {
  isActive: boolean;
  effectiveFrom: string;
};

type SavedScheduleSnapshot = {
  slotsSignature: string;
  isActive: boolean;
  effectiveFrom: string;
  offlineCenterId: number | null;
};

const buildSlotsSignature = (slotsByDay: Record<string, Slot[]>): string => {
  const byDay: Record<string, Array<{ startTime: string; endTime: string }>> = {};
  for (const d of DAYS) {
    byDay[d.key] = sortSlots(slotsByDay[d.key] || []).map((slot) => ({
      startTime: normalizeTemplateTime(slot.startTime),
      endTime: normalizeTemplateTime(slot.endTime),
    }));
  }
  return JSON.stringify(byDay);
};

const buildSavedScheduleSnapshot = (
  slotsByDay: Record<string, Slot[]>,
  form: ScheduleFormSnapshot,
  offlineCenterId: number | null
): SavedScheduleSnapshot => ({
  slotsSignature: buildSlotsSignature(slotsByDay),
  isActive: form.isActive,
  effectiveFrom: form.effectiveFrom.trim(),
  offlineCenterId,
});

const getScheduleApiErrorDetail = (err: unknown): string | undefined => {
  const ax = err as { response?: { data?: { message?: string; error?: string } | string } };
  const raw = ax.response?.data;
  if (typeof raw === "string") return raw;
  if (raw && typeof raw === "object") return raw.message || raw.error;
  return undefined;
};

const Slots: React.FC<SlotsProps> = ({ teacherId, teacherProfileData }) => {
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("10:00");
  /** When set, inline “add slot” panel is open for that day (package-card ►/▼ pattern). */
  const [expandedAddSlotDayKey, setExpandedAddSlotDayKey] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [aeUnavailableSlots, setAeUnavailableSlots] = useState<AeUnavailabilitySlotResponse[]>([]);
  const [leaveConfigs, setLeaveConfigs] = useState<LeaveDateConfig[]>([]);
  const [draftLeaveDate, setDraftLeaveDate] = useState("");
  const [draftLeaveType, setDraftLeaveType] = useState<LeaveDateConfig["type"]>("CUSTOM_SLOTS");
  const [draftLeaveSlots, setDraftLeaveSlots] = useState<Slot[]>([]);
  const [draftFullDay, setDraftFullDay] = useState(false);
  const [isLeaveModalOpen, setIsLeaveModalOpen] = useState(false);
  const [leaveModalTab, setLeaveModalTab] = useState<"apply" | "view">("apply");
  const [copyFromByDay, setCopyFromByDay] = useState<Record<string, string>>({});
  const [slotsByDay, setSlotsByDay] = useState<Record<string, Slot[]>>(() => createEmptySlotsByDay());
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [deletingUnavailableId, setDeletingUnavailableId] = useState<number | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ date: string; timings: { id: number; startTime: string; endTime: string }[] } | null>(null);
  const [deleteConfirmBusy, setDeleteConfirmBusy] = useState(false);
  const [publishConfirmOpen, setPublishConfirmOpen] = useState(false);
  const [publishDateModalOpen, setPublishDateModalOpen] = useState(false);
  const [publishEffectiveFrom, setPublishEffectiveFrom] = useState("");
  const [scheduleMeta, setScheduleMeta] = useState<{
    effectiveFrom?: string;
    isActive?: boolean;
  } | null>(null);
  const [templateForm, setTemplateForm] = useState({
    effectiveFrom: "",
    isActive: true,
  });
  const [hasPersistedServerTemplate, setHasPersistedServerTemplate] = useState(false);
  const [offlineCenterId, setOfflineCenterId] = useState<number | null>(null);
  const [clearingSchedule, setClearingSchedule] = useState(false);
  const [clearingDayKey, setClearingDayKey] = useState<string | null>(null);
  /** `null` = closed; `"__ALL__"` = reset all on server; otherwise a `DayOfWeek` key for per-day clear. */
  const [clearConfirmTarget, setClearConfirmTarget] = useState<string | null>(null);
  const [clearConfirmBusy, setClearConfirmBusy] = useState(false);
  const [resetDeleteFromDate, setResetDeleteFromDate] = useState("");
  const [savedScheduleSnapshot, setSavedScheduleSnapshot] = useState<SavedScheduleSnapshot | null>(
    null
  );

  const CLEAR_CONFIRM_ALL = "__ALL__";
  type ResetAllMode = "default" | "templatesOnly" | "fromDate";

  const getResetAllDeleteOptions = (
    mode: ResetAllMode,
    deleteFrom?: string
  ): DeleteTeacherScheduleTemplatesOptions | undefined => {
    if (mode === "templatesOnly") return { deleteSlots: false };
    if (mode === "fromDate" && deleteFrom?.trim()) return { deleteFrom: deleteFrom.trim() };
    return undefined;
  };

  const getResetAllSuccessMessage = (mode: ResetAllMode, deleteFrom?: string): string => {
    if (mode === "templatesOnly") {
      return "All schedule templates deleted. Existing slots were kept.";
    }
    if (mode === "fromDate" && deleteFrom?.trim()) {
      return `Schedule templates deleted. Slots from ${deleteFrom.trim()} onward were pruned.`;
    }
    return "All schedule templates and future slots deleted successfully.";
  };

  const refreshSavedScheduleSnapshot = useCallback(
    (slots: Record<string, Slot[]>, form: ScheduleFormSnapshot, centerId: number | null) => {
      setSavedScheduleSnapshot(buildSavedScheduleSnapshot(slots, form, centerId));
    },
    []
  );

  const resetToEmptyScheduleState = useCallback(() => {
    const emptySlots = createEmptySlotsByDay();
    const emptyForm = { effectiveFrom: "", isActive: true };
    setScheduleMeta(null);
    setSlotsByDay(emptySlots);
    setHasPersistedServerTemplate(false);
    setOfflineCenterId(null);
    setTemplateForm(emptyForm);
    setCopyFromByDay({});
    setExpandedAddSlotDayKey(null);
    refreshSavedScheduleSnapshot(emptySlots, emptyForm, null);
  }, [refreshSavedScheduleSnapshot]);
  const teacherProfileDetails = teacherProfileData?.teacherProfileDetails;

  const config = useContext(ConfigContext);

  // Same source as the Profile tab Category field (FormProfile): top-level or nested name, else config lookup by id
  const teacherCategoryName: string = useMemo(() => {
    const savedName = String(
      teacherProfileData?.courseCategory || teacherProfileDetails?.courseCategory || ""
    ).trim();
    if (savedName) return savedName;
    const categoryId =
      teacherProfileDetails?.aeCourseCategoryId ??
      teacherProfileDetails?.courseCategoryId ??
      teacherProfileData?.aeCourseCategoryId ??
      teacherProfileData?.courseCategoryId;
    if (categoryId == null || categoryId === "") return "";
    const match = (config?.AirlineCourseCategory || []).find(
      (category: { categoryId: number; id: number }) =>
        String(category.categoryId) === String(categoryId) || String(category.id) === String(categoryId)
    );
    return match?.name || "";
  }, [teacherProfileData, teacherProfileDetails, config?.AirlineCourseCategory]);

  /** Offline teachers must pin their weekly template to one offline centre. */
  const isOfflineTeacher = teacherProfileDetails?.isBatchOffline === true;

  const offlineCentreOptions = useMemo(
    () =>
      (config?.offlineCentres || []).map((centre: { centreId: number; name: string }) => ({
        value: centre.centreId,
        label: centre.name || "Unknown Centre",
      })),
    [config?.offlineCentres]
  );

  const selectedOfflineCentre = useMemo(
    () =>
      offlineCentreOptions.find(
        (option: { value: number }) => option.value === offlineCenterId
      ) || null,
    [offlineCentreOptions, offlineCenterId]
  );

  const needsOfflineCentre = isOfflineTeacher && offlineCenterId == null;

  /**
   * The centre is fixed once the server holds a template that names one. A persisted
   * template with no centre stays editable, otherwise the teacher can neither set the
   * required centre nor publish.
   */
  const isOfflineCentreLocked = hasPersistedServerTemplate && offlineCenterId != null;

  /** Paid teachers: 1-hour blocks. Non-paid (`!is1to1Enabled`): 30-minute blocks for API/slots. */
  const slotSplitStepMinutes = useMemo<30 | 60>(
    () => (teacherProfileDetails?.is1to1Enabled ? 60 : 30),
    [teacherProfileDetails?.is1to1Enabled]
  );

  const timeOptions = useMemo(
    () => (slotSplitStepMinutes === 30 ? TIME_OPTIONS_30 : TIME_OPTIONS_60),
    [slotSplitStepMinutes]
  );

  const applyLoadedSchedule = useCallback(
    (data: TeacherScheduleTemplatesResponse) => {
      const loadedSlots = mapScheduleTemplatesToSlotsByDay(data);
      const loadedForm = {
        effectiveFrom: data.effectiveFrom ?? "",
        isActive: data.isActive !== false,
      };
      setScheduleMeta({
        effectiveFrom: data.effectiveFrom,
        isActive: data.isActive,
      });
      const loadedOfflineCenterId = data.offlineCenterId ?? null;
      setSlotsByDay(loadedSlots);
      setTemplateForm(loadedForm);
      setOfflineCenterId(loadedOfflineCenterId);
      setHasPersistedServerTemplate(scheduleResponseHasPersistedTemplates(data));
      setCopyFromByDay({});
      setExpandedAddSlotDayKey(null);
      refreshSavedScheduleSnapshot(loadedSlots, loadedForm, loadedOfflineCenterId);
    },
    [refreshSavedScheduleSnapshot]
  );

  useEffect(() => {
    if (!teacherId) return;
    let cancelled = false;
    const load = async () => {
      setScheduleLoading(true);
      try {
        const [templateData, aeUnavailabilityData] = await Promise.all([
          getTeacherScheduleTemplates(teacherId),
          getAeUnavailability(teacherId).catch(() => null),
        ]);
        if (cancelled) return;
        applyLoadedSchedule(templateData);
        const incomingAeSlots = aeUnavailabilityData?.slots || [];
        setAeUnavailableSlots(incomingAeSlots);
        setSlotsByDay(mapScheduleTemplatesToSlotsByDay(templateData));
      } catch (err: unknown) {
        if (cancelled) return;
        const status = (err as { response?: { status?: number } })?.response?.status;
        resetToEmptyScheduleState();
        if (status !== 404) {
          toast.error("Could not load weekly schedule.");
        }
      } finally {
        if (!cancelled) setScheduleLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [teacherId, applyLoadedSchedule, resetToEmptyScheduleState]);

  const totalSlots = useMemo(
    () => Object.values(slotsByDay).reduce((acc, v) => acc + v.length, 0),
    [slotsByDay]
  );

  const hasScheduleChanges = useMemo(() => {
    if (!savedScheduleSnapshot) return false;
    const current = buildSavedScheduleSnapshot(slotsByDay, templateForm, offlineCenterId);
    return (
      current.slotsSignature !== savedScheduleSnapshot.slotsSignature ||
      current.isActive !== savedScheduleSnapshot.isActive ||
      current.effectiveFrom !== savedScheduleSnapshot.effectiveFrom ||
      current.offlineCenterId !== savedScheduleSnapshot.offlineCenterId
    );
  }, [slotsByDay, templateForm, savedScheduleSnapshot, offlineCenterId]);

  const canPublishSchedule =
    !scheduleLoading &&
    !saving &&
    !clearingSchedule &&
    totalSlots > 0 &&
    hasScheduleChanges &&
    !needsOfflineCentre;

  const copyFromGloballyDisabled = totalSlots === 0 || scheduleLoading || saving;

  /** Adds consecutive slots (30 or 60 min steps per `slotSplitStepMinutes`); duplicates for that day are skipped. */
  const addSplitSlotsForDay = (dayKey: string, sTime: string, eTime: string) => {
    if (sTime === eTime) {
      toast.error("End time should be greater than start time.");
      return;
    }
    const { startMin, endMin } = resolveScheduleRangeEndMinutes(sTime, eTime);
    if (endMin <= startMin) {
      toast.error("End time should be greater than start time.");
      return;
    }
    const step = slotSplitStepMinutes;
    const { segments, hasPartialTail } = splitRangeIntoFixedSegments(sTime, eTime, step);
    if (segments.length === 0) {
      toast.error(
        step === 30
          ? "Select a range that covers at least 30 minutes."
          : "Select a range that covers at least one full hour."
      );
      return;
    }
    const stepLabel = step === 30 ? "30-minute" : "one-hour";
    const priorSlots = slotsByDay[dayKey] || [];
    const toAdd = segments.filter(
      (seg) => !priorSlots.some((slot) => slot.startTime === seg.startTime && slot.endTime === seg.endTime)
    );
    if (toAdd.length === 0) {
      toast.info(`Those ${stepLabel} slots are already on ${getDayLabel(dayKey)}.`);
      return;
    }
    const baseId = Date.now();
    setSlotsByDay((prev) => {
      const daySlots = prev[dayKey] || [];
      const fresh = segments.filter(
        (seg) => !daySlots.some((slot) => slot.startTime === seg.startTime && slot.endTime === seg.endTime)
      );
      if (fresh.length === 0) return prev;
      const updated = [
        ...daySlots,
        ...fresh.map((seg, i) => ({
          id: `${dayKey}-${seg.startTime}-${seg.endTime}-${baseId}-${i}`,
          startTime: seg.startTime,
          endTime: seg.endTime,
          specialSlot: false,
        })),
      ];
      return { ...prev, [dayKey]: sortSlots(updated) };
    });
    const tailNote =
      hasPartialTail && step === 30
        ? " Any period shorter than 30 minutes at the end was not added."
        : hasPartialTail && step === 60
          ? " Any period shorter than one hour at the end was not added."
          : "";
    const slotWord = step === 30 ? "30-minute" : "one-hour";
    toast.success(`${toAdd.length} new ${slotWord} slot(s) on ${getDayLabel(dayKey)}.${tailNote}`);
  };

  const closeExpandedAddSlotPanel = () => {
    setExpandedAddSlotDayKey(null);
  };

  const toggleExpandedAddSlot = (dayKey: string) => {
    setExpandedAddSlotDayKey((prev) => (prev === dayKey ? null : dayKey));
  };

  const submitAddSlotForDay = (dayKey: string) => {
    addSplitSlotsForDay(dayKey, startTime, endTime);
  };

  const getNextTimeOption = (time: string) => {
    const currentIndex = timeOptions.findIndex((opt) => opt.value === time);
    if (currentIndex === -1 || currentIndex === timeOptions.length - 1) return time;
    return timeOptions[currentIndex + 1].value;
  };

  const handleStartTimeChange = (value: string) => {
    setStartTime(value);
    if (isOvernightScheduleEndTime(value, endTime)) return;
    if (value >= endTime) {
      setEndTime(getNextTimeOption(value));
    }
  };

  const deleteDaySlot = (dayKey: string, slotId: string) => {
    setSlotsByDay((prev) => ({
      ...prev,
      [dayKey]: (prev[dayKey] || []).filter((slot) => slot.id !== slotId),
    }));
  };

  const openClearDayConfirm = (dayKey: string) => {
    if ((slotsByDay[dayKey] || []).length === 0) return;
    setClearConfirmTarget(dayKey);
  };

  const performClearDaySlots = async (dayKey: string) => {
    if ((slotsByDay[dayKey] || []).length === 0) {
      return;
    }

    if (!hasPersistedServerTemplate) {
      setSlotsByDay((prev) => ({ ...prev, [dayKey]: [] }));
      trackEvent(MixpanelEvents.TEACHER_SCHEDULE_DAY_CLEARED, {
        teacherId,
        teacherName: `${teacherProfileDetails.firstName} ${teacherProfileDetails.lastName}`,
        dayKey,
        dayLabel: getDayLabel(dayKey),
        syncedToServer: false,
      });
      return;
    }

    if (!teacherId) {
      toast.error("Missing teacher id.");
      throw new Error("Missing teacher id.");
    }
    if (needsOfflineCentre) {
      toast.error("Select an offline centre before clearing slots.");
      throw new Error("Offline centre is required.");
    }
    setClearingDayKey(dayKey);
    try {
      const putResponse = await updateTeacherScheduleTemplates(teacherId, {
        effectiveFrom: getTodayDateString(),
        applyImmediately: true,
        ...(isOfflineTeacher && offlineCenterId != null ? { offlineCenterId } : {}),
        days: [{ day: dayKey, timings: [] }],
      });
      const nextSlots = putResponse.days?.length
        ? mergeSchedulePutDaysIntoSlots(slotsByDay, putResponse)
        : { ...slotsByDay, [dayKey]: [] };
      const nextForm = {
        effectiveFrom: putResponse.effectiveFrom ?? templateForm.effectiveFrom,
        isActive: putResponse.isActive ?? templateForm.isActive,
      };
      const nextOfflineCenterId = putResponse.offlineCenterId ?? offlineCenterId;
      setSlotsByDay(nextSlots);
      setScheduleMeta((m) => ({
        effectiveFrom: putResponse.effectiveFrom ?? m?.effectiveFrom,
        isActive: putResponse.isActive ?? m?.isActive,
      }));
      setTemplateForm((prev) => ({
        ...prev,
        effectiveFrom: nextForm.effectiveFrom,
        isActive: nextForm.isActive,
      }));
      setOfflineCenterId(nextOfflineCenterId);
      refreshSavedScheduleSnapshot(nextSlots, nextForm, nextOfflineCenterId);
      toast.success(`Slots cleared for ${getDayLabel(dayKey)}.`);
      trackEvent(MixpanelEvents.TEACHER_SCHEDULE_DAY_CLEARED, {
        teacherId,
        teacherName: `${teacherProfileDetails.firstName} ${teacherProfileDetails.lastName}`,
        dayKey,
        dayLabel: getDayLabel(dayKey),
        syncedToServer: true,
      });
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      const detail = getScheduleApiErrorDetail(err);
      if (status === 409) {
        toast.error(detail || "A slot overlaps an existing slot for that day.");
      } else {
        toast.error(detail || `Failed to clear slots for ${getDayLabel(dayKey)}.`);
      }
      throw err;
    } finally {
      setClearingDayKey(null);
    }
  };

  const copyDaySlots = (fromDayKey: string, toDayKey: string) => {
    if (!fromDayKey || fromDayKey === toDayKey) return;
    setSlotsByDay((prev) => {
      const sourceSlots = prev[fromDayKey] || [];
      const replaced = sourceSlots.map((slot) => ({
        ...slot,
        id: `${toDayKey}-${slot.startTime}-${slot.endTime}-${Date.now()}-${Math.random()}`,
      }));

      return {
        ...prev,
        [toDayKey]: sortSlots(replaced),
      };
    });
    toast.success(`Copied slots from ${fromDayKey.toLowerCase()} to ${toDayKey.toLowerCase()}.`);
  };

  const saveSchedule = async (options?: { applyImmediately?: boolean; effectiveFrom?: string }) => {
    if (needsOfflineCentre) {
      toast.error("Select an offline centre before saving.");
      return;
    }

    const requestedEffectiveFrom = options?.effectiveFrom?.trim();
    if (requestedEffectiveFrom && isPastDateString(requestedEffectiveFrom)) {
      toast.error("Past dates are not allowed.");
      return;
    }

    const offlineCentrePayload =
      isOfflineTeacher && offlineCenterId != null ? { offlineCenterId } : {};

    const daysForPut: UpdateTeacherScheduleTemplateBody["days"] = DAYS.map((d) => ({
      day: d.key,
      timings: sortSlots(slotsByDay[d.key] || []).map((slot) => ({
        startTime: toApiTime(slot.startTime),
        endTime: toApiTime(slot.endTime),
      })),
    }));

    const daysForPost = daysForPut.filter((block) => block.timings.length > 0);

    if (hasPersistedServerTemplate) {
      const putBody: UpdateTeacherScheduleTemplateBody = {
        effectiveFrom: options?.effectiveFrom?.trim() || getTodayDateString(),
        isActive: templateForm.isActive,
        ...offlineCentrePayload,
        days: daysForPut,
        ...(options?.applyImmediately === true ? { applyImmediately: true } : {}),
      };

      setSaving(true);
      try {
        await updateTeacherScheduleTemplates(teacherId, putBody);
        const data = await getTeacherScheduleTemplates(teacherId);
        applyLoadedSchedule(data);
        toast.success("Weekly schedule updated.");
        trackEvent(MixpanelEvents.TEACHER_SCHEDULE_PUBLISHED, {
          teacherId,
          teacherName: `${teacherProfileDetails.firstName} ${teacherProfileDetails.lastName}`,
          action: "UPDATE",
          totalSlots,
          effectiveFrom: putBody.effectiveFrom,
          ...(options?.applyImmediately === true ? { applyImmediately: true } : {}),
        });
      } catch (err: unknown) {
        const ax = err as {
          response?: { status?: number; data?: { message?: string; error?: string } | string };
        };
        const status = ax.response?.status;
        const raw = ax.response?.data;
        const detail =
          typeof raw === "string"
            ? raw
            : raw && typeof raw === "object"
              ? raw.message || raw.error
              : undefined;
        if (status === 409) {
          toast.error(detail || "A slot overlaps an existing slot for that day.");
        } else {
          toast.error(detail || "Failed to update weekly schedule.");
        }
      } finally {
        setSaving(false);
      }
      return;
    }

    if (daysForPost.length === 0) {
      toast.info("Please add at least one slot before saving.");
      return;
    }

    const effectiveFrom = templateForm.effectiveFrom.trim();
    if (!effectiveFrom) {
      toast.error("Effective date is required.");
      return;
    }
    if (isPastDateString(effectiveFrom)) {
      toast.error("Past dates are not allowed.");
      return;
    }

    const postBody: CreateTeacherScheduleTemplateBody = {
      effectiveFrom,
      isActive: templateForm.isActive,
      ...offlineCentrePayload,
      days: daysForPost,
    };

    setSaving(true);
    try {
      const data = await createTeacherScheduleTemplates(teacherId, postBody);
      applyLoadedSchedule(data);
      toast.success("Weekly schedule created.");
      trackEvent(MixpanelEvents.TEACHER_SCHEDULE_PUBLISHED, {
        teacherId,
        teacherName: `${teacherProfileDetails.firstName} ${teacherProfileDetails.lastName}`,
        action: "CREATE",
        effectiveFrom: postBody.effectiveFrom,
        totalSlots,
      });
    } catch (err: unknown) {
      const ax = err as {
        response?: { status?: number; data?: { message?: string; error?: string } | string };
      };
      const status = ax.response?.status;
      const raw = ax.response?.data;
      const detail =
        typeof raw === "string"
          ? raw
          : raw && typeof raw === "object"
            ? raw.message || raw.error
            : undefined;
      if (status === 409) {
        toast.error(detail || "A slot overlaps an existing slot for that day.");
      } else {
        toast.error(detail || "Failed to create weekly schedule.");
      }
    } finally {
      setSaving(false);
    }
  };

  const openClearAllConfirm = () => {
    setResetDeleteFromDate(getTodayDateString());
    setClearConfirmTarget(CLEAR_CONFIRM_ALL);
  };

  const performClearAllSchedules = async (mode: ResetAllMode = "default", deleteFrom?: string) => {
    if (!teacherId) {
      toast.error("Missing teacher id.");
      throw new Error("Missing teacher id.");
    }
    setClearingSchedule(true);
    try {
      await deleteTeacherScheduleTemplates(teacherId, getResetAllDeleteOptions(mode, deleteFrom));
      resetToEmptyScheduleState();
      toast.success(getResetAllSuccessMessage(mode, deleteFrom));
      trackEvent(MixpanelEvents.TEACHER_SCHEDULE_RESET_ALL, {
        teacherId,
        teacherName: `${teacherProfileDetails.firstName} ${teacherProfileDetails.lastName}`,
        outcome: "deleted",
        resetMode: mode,
        deleteFrom: deleteFrom?.trim() || undefined,
      });
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      const detail = getScheduleApiErrorDetail(err);
      if (status === 404) {
        resetToEmptyScheduleState();
        toast.info(detail || "No schedule templates found for this teacher.");
        trackEvent(MixpanelEvents.TEACHER_SCHEDULE_RESET_ALL, {
          teacherId,
          teacherName: `${teacherProfileDetails.firstName} ${teacherProfileDetails.lastName}`,
          outcome: "none_on_server_cleared_local",
          resetMode: mode,
          deleteFrom: deleteFrom?.trim() || undefined,
        });
      } else {
        toast.error(detail || "Failed to remove schedule templates.");
        throw err;
      }
    } finally {
      setClearingSchedule(false);
    }
  };

  const closeClearConfirm = () => {
    if (clearConfirmBusy) return;
    setClearConfirmTarget(null);
    setResetDeleteFromDate("");
  };

  const confirmClearAll = async (mode: ResetAllMode) => {
    if (mode === "fromDate") {
      if (!resetDeleteFromDate.trim()) {
        toast.error("Please select a date to delete slots from.");
        return;
      }
      if (isPastDateString(resetDeleteFromDate)) {
        toast.error("Past dates are not allowed.");
        return;
      }
    }
    setClearConfirmBusy(true);
    try {
      await performClearAllSchedules(mode, resetDeleteFromDate.trim());
      setClearConfirmTarget(null);
      setResetDeleteFromDate("");
    } catch {
      /* toasts handled in perform* */
    } finally {
      setClearConfirmBusy(false);
    }
  };

  const confirmClear = async () => {
    const target = clearConfirmTarget;
    if (!target || target === CLEAR_CONFIRM_ALL) return;
    setClearConfirmBusy(true);
    try {
      await performClearDaySlots(target);
      setClearConfirmTarget(null);
    } catch {
      /* toasts handled in perform* */
    } finally {
      setClearConfirmBusy(false);
    }
  };

  const openLeaveModal = (tab: "apply" | "view" = "apply") => {
    setDraftLeaveDate("");
    setDraftLeaveType("CUSTOM_SLOTS");
    setDraftLeaveSlots([]);
    setDraftFullDay(false);
    setLeaveConfigs([]);
    setLeaveModalTab(tab);
    setIsLeaveModalOpen(true);
  };

  const addLeaveDateConfig = () => {
    if (!draftLeaveDate) {
      toast.error("Please choose a date first.");
      return;
    }
    const dayKey = getDayKeyFromDate(draftLeaveDate);
    const nextConfig: LeaveDateConfig = {
      id: `cfg-${draftLeaveDate}-${Date.now()}`,
      date: draftLeaveDate,
      dayKey,
      type: "CUSTOM_SLOTS",
      slots: sortSlots(draftLeaveSlots),
    };
    setLeaveConfigs((prev) => {
      const filtered = prev.filter((cfg) => cfg.date !== draftLeaveDate);
      return [...filtered, nextConfig].sort((a, b) => a.date.localeCompare(b.date));
    });
    // toast.success(`Added ${draftLeaveDate}.`);
  };

  const removeLeaveDateConfig = (configId: string) => {
    setLeaveConfigs((prev) => prev.filter((cfg) => cfg.id !== configId));
  };

  const removeDraftSlot = (slotId: string) => {
    setDraftLeaveSlots((prev) => prev.filter((slot) => slot.id !== slotId));
  };

  const handleConfirmLeaveChanges = async () => {
    if (!draftLeaveDate) {
      toast.error("Please choose a date first.");
      return;
    }

    // UX: Apply should work without forcing a separate "+ Add" click.
    const dayKey = getDayKeyFromDate(draftLeaveDate);
    const currentDraftConfig: LeaveDateConfig = {
      id: `cfg-${draftLeaveDate}-adhoc`,
      date: draftLeaveDate,
      dayKey,
      type: "CUSTOM_SLOTS",
      slots: sortSlots(draftLeaveSlots),
    };

    const effectiveConfigs: LeaveDateConfig[] = (() => {
      if (leaveConfigs.length === 0) return [currentDraftConfig];
      const hasCurrent = leaveConfigs.some((cfg) => cfg.date === draftLeaveDate);
      return hasCurrent ? leaveConfigs : [...leaveConfigs, currentDraftConfig];
    })();

    // Full-day: one API entry per date, isFullDay=true, no timings (includes queued + current draft)
    if (draftFullDay) {
      const slotsPayload = effectiveConfigs.map((cfg) => ({
        date: cfg.date,
        isFullDay: true as const,
        timings: [],
      }));
      try {
        await createAeUnavailability(teacherId, { slots: slotsPayload });
        try {
          const [templateData, refreshedAe] = await Promise.all([
            getTeacherScheduleTemplates(teacherId),
            getAeUnavailability(teacherId).catch(() => null),
          ]);
          applyLoadedSchedule(templateData);
          setAeUnavailableSlots(refreshedAe?.slots || []);
          setSlotsByDay(mapScheduleTemplatesToSlotsByDay(templateData));
        } catch { /* ignore re-fetch errors */ }
        toast.success(
          slotsPayload.length === 1
            ? `Full day leave applied for ${slotsPayload[0].date}.`
            : `Full day leave applied for ${slotsPayload.length} dates.`
        );
        trackEvent(MixpanelEvents.TEACHER_AE_LEAVE_APPLIED, {
          teacherId,
          teacherName: `${teacherProfileDetails.firstName} ${teacherProfileDetails.lastName}`,
          dates: slotsPayload.map((s) => s.date),
          totalDates: slotsPayload.length,
          leaveType: "FULL_DAY",
        });
        setLeaveModalTab("view");
      } catch (err) {
        toast.error(getScheduleApiErrorDetail(err) || "Failed to apply full day leave.");
      }
      return;
    }

    const slotsPayload = effectiveConfigs
      .map((cfg) => {
        const sourceSlots = sortSlots(slotsByDay[cfg.dayKey] || []);
        const remainingSlots = sortSlots(cfg.slots || []);
        const removedTimings = sourceSlots
          .filter(
            (slot) =>
              !remainingSlots.some(
                (left) => left.startTime === slot.startTime && left.endTime === slot.endTime
              )
          )
          .map((slot) => ({
            startTime: toApiTime(slot.startTime),
            endTime: toApiTime(slot.endTime),
          }));

        if (removedTimings.length === 0) return null;
        return {
          date: cfg.date,
          isFullDay: false,
          timings: removedTimings,
        };
      })
      .filter(Boolean) as Array<{ date: string; isFullDay: false; timings: Array<{ startTime: string; endTime: string }> }>;

    if (slotsPayload.length === 0) {
      toast.info("No slots were removed to apply as unavailability.");
      return;
    }

    try {
      await createAeUnavailability(teacherId, { slots: slotsPayload });
      // Re-sync from backend after apply: first schedule template GET, then AE unavailability GET.
      try {
        const [templateData, refreshedAe] = await Promise.all([
          getTeacherScheduleTemplates(teacherId),
          getAeUnavailability(teacherId).catch(() => null),
        ]);
        applyLoadedSchedule(templateData);
        const incomingAeSlots = refreshedAe?.slots || [];
        setAeUnavailableSlots(incomingAeSlots);
        setSlotsByDay(mapScheduleTemplatesToSlotsByDay(templateData));
      } catch {
        // Fallback to local optimistic update if re-fetch fails.
        setSlotsByDay((prev) => {
          const next = { ...prev };
          effectiveConfigs.forEach((cfg) => {
            next[cfg.dayKey] = sortSlots(cfg.slots);
          });
          return next;
        });
      }

      toast.success(`Applied updates for ${slotsPayload.length} date(s).`);
      trackEvent(MixpanelEvents.TEACHER_AE_LEAVE_APPLIED, {
        teacherId,
        teacherName: `${teacherProfileDetails.firstName} ${teacherProfileDetails.lastName}`,
        dates: slotsPayload.map((s) => s.date),
        totalDates: slotsPayload.length,
        leaveType: "CUSTOM_SLOTS",
      });
      setLeaveModalTab("view");
    } catch (err) {
      const detail = getScheduleApiErrorDetail(err);
      toast.error(detail || "Failed to apply leave slots.");
    }
  };

  const deleteUnavailableTiming = async (unavailabilityId: number) => {
    if (!teacherId) return;
    setDeletingUnavailableId(unavailabilityId);
    try {
      await deleteAeUnavailabilityById(teacherId, unavailabilityId);
      const refreshed = await getAeUnavailability(teacherId);
      setAeUnavailableSlots(refreshed?.slots || []);
      toast.success("Unavailability timing deleted.");
    } catch (err) {
      const detail = getScheduleApiErrorDetail(err);
      toast.error(detail || "Failed to delete unavailability timing.");
    } finally {
      setDeletingUnavailableId(null);
    }
  };

  const confirmDeleteSlots = async () => {
    if (!deleteConfirm || !teacherId) return;
    setDeleteConfirmBusy(true);
    let successCount = 0;
    let failCount = 0;
    for (const t of deleteConfirm.timings) {
      try {
        await deleteAeUnavailabilityById(teacherId, t.id);
        successCount++;
      } catch {
        failCount++;
      }
    }
    const refreshed = await getAeUnavailability(teacherId).catch(() => null);
    setAeUnavailableSlots(refreshed?.slots || []);
    setDeleteConfirmBusy(false);
    setDeleteConfirm(null);
    if (failCount === 0) {
      toast.success(`${successCount} slot${successCount !== 1 ? "s" : ""} deleted successfully.`);
    } else {
      toast.warning(`${successCount} deleted, ${failCount} failed.`);
    }
  };

  return (
    <div className="tp-slots">
      <div className="tp-slots__scroll">
      {scheduleLoading && <Loader label="Loading weekly schedule…" />}
      <Card className="tp-slots__bar">
        <div className="tp-slots__bar-row">
          <span className="tp-slots__bar-label">
            {`${teacherProfileDetails?.firstName || "Teacher"} is `}
            {teacherCategoryName ? (
              <>
                tagged to <Badge tone="accent">{teacherCategoryName}</Badge> category
              </>
            ) : (
              <Badge tone="warning">not tagged to any category</Badge>
            )}
          </span>
        </div>
      </Card>
      {/* {!scheduleLoading && (
        <div
          style={{
            border: "1px solid #e4e5ef",
            borderRadius: 14,
            padding: 12,
            marginBottom: 12,
            background: "#fff",
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 600, color: "#5e56b9", marginBottom: 6 }}>
            Schedule validity
          </div>
          <div style={{ fontSize: 11, color: "#757a85", marginBottom: 10 }}>
            {hasPersistedServerTemplate
              ? "Saving updates the template (PUT): all seven days are sent; empty days clear slots. Each save sends effective from as today's date."
              : "No weekly template on the server yet; the first save creates one (POST) with the effective-from date you pick."}
          </div>
          <Row className="g-2 align-items-end">
            <Col md="3" sm="6">
              <label style={{ fontSize: 12, color: "#6c7280", marginBottom: 6, display: "block" }}>
                Effective from {!hasPersistedServerTemplate && <span style={{ color: "#c87f8b" }}>*</span>}
              </label>
              <Input
                type="date"
                bsSize="sm"
                disabled={hasPersistedServerTemplate}
                title={
                  hasPersistedServerTemplate
                    ? "On PUT, effective from is sent automatically as today's date (see live publish flow)."
                    : undefined
                }
                style={{ height: 34, fontSize: 12 }}
                value={templateForm.effectiveFrom}
                onChange={(e) => setTemplateForm((p) => ({ ...p, effectiveFrom: e.target.value }))}
              />
            </Col>
            <Col md="3" sm="6">
              <label
                style={{
                  fontSize: 12,
                  color: "#6c7280",
                  marginBottom: 0,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  cursor: "pointer",
                }}
              >
                <Input
                  type="checkbox"
                  checked={templateForm.isActive}
                  onChange={(e) => setTemplateForm((p) => ({ ...p, isActive: e.target.checked }))}
                  style={{ width: 18, height: 18, cursor: "pointer" }}
                />
                Active
              </label>
            </Col>
          </Row>
          {scheduleMeta?.effectiveFrom && (
            <div style={{ fontSize: 11, color: "#9aa1af", marginTop: 8 }}>
              Last loaded from server for this teacher.
            </div>
          )}
        </div>
      )} */}
    {
      isOfflineTeacher && (
        <Card className="tp-slots__bar">
          <div className="tp-slots__bar-row">
            <Field
              id="slots-offline-centre"
              label="Offline centre"
              required
              hint={
                isOfflineCentreLocked
                  ? "Locked once the weekly template exists."
                  : "Slots are published to this centre."
              }
              className="tp-slots__centre"
            >
              {({ id }) => (
                <Select
                  inputId={id}
                  value={selectedOfflineCentre}
                  onChange={(option: { value: number } | null) =>
                    setOfflineCenterId(option ? option.value : null)
                  }
                  options={offlineCentreOptions}
                  placeholder="Select Centre"
                  className="react-select-container"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  isDisabled={isOfflineCentreLocked}
                />
              )}
            </Field>
          </div>
        </Card>
      )
    }
    {
      !teacherProfileDetails?.is1to1Enabled && (
        <Card className="tp-slots__bar">
          <div className="tp-slots__bar-row tp-slots__bar-row--between">
            <span className="tp-slots__bar-label">
              Leave management
              {aeUnavailableSlots.length > 0 && (
                <Badge tone="danger">
                  {aeUnavailableSlots.length} date{aeUnavailableSlots.length !== 1 ? "s" : ""} blocked
                </Badge>
              )}
            </span>
            <div className="tp-slots__actions">
              <Button variant="accent" size="sm" onClick={() => openLeaveModal("apply")}>
                Block Calendar
              </Button>
              <Button
                variant="secondary"
                size="sm"
                icon={<Calendar size={14} aria-hidden />}
                onClick={() => openLeaveModal("view")}
                title="View & manage applied leaves"
              >
                View Blocked Dates
              </Button>
            </div>
          </div>
        </Card>
      )
    }
      <Modal isOpen={isLeaveModalOpen} toggle={() => setIsLeaveModalOpen(false)} size="lg">
        <ModalHeader toggle={() => setIsLeaveModalOpen(false)}>
          Manage Leave & Unavailability
        </ModalHeader>

        {/* Tab bar */}
        <div className="tp-slots__tabs">
          <SegmentedControl
            label="Leave view"
            size="sm"
            value={leaveModalTab}
            onChange={(tab) => {
              setLeaveModalTab(tab as "apply" | "view");
            }}
            options={[
              { value: "apply", label: "Block Calendar" },
              { value: "view", label: "View Blocked Dates" },
            ]}
          />
        </div>

        {/* ── TAB A: Apply Leave ─────────────────────────────────── */}
        {leaveModalTab === "apply" && (
          <>
            <ModalBody className="tp-slots__stack">
              {/* Step 1: Pick date */}
              <div className="tp-slots__step">
                <div className="tp-slots__step-head">
                  <Badge tone="primary" solid>1</Badge>
                  <span className="tp-slots__step-title">Select date</span>
                </div>
                <TextInput
                  type="date"
                  size="sm"
                  aria-label="Leave date"
                  className="tp-slots__date"
                  value={draftLeaveDate}
                  min={(() => { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() + 1); return d.toISOString().split("T")[0]; })()}
                  onChange={(e) => {
                    const nextDate = e.target.value;
                    setDraftLeaveDate(nextDate);
                    if (!nextDate) {
                      setDraftLeaveSlots([]);
                      return;
                    }
                    const dayKey = getDayKeyFromDate(nextDate);
                    setDraftLeaveSlots(sortSlots([...(slotsByDay[dayKey] || [])]));
                  }}
                />
                {!draftLeaveDate && (
                  <span className="tp-slots__hint">Choose a date to continue.</span>
                )}
              </div>

              {/* Full Day toggle */}
              <Switch
                tone="danger"
                checked={draftFullDay}
                onChange={() => setDraftFullDay((v) => !v)}
                label="Full Day Leave"
                description="Mark entire day unavailable (all slots blocked)"
              />

              {/* Steps 2 & 3 — hidden when full day is on */}
              {!draftFullDay && (<>
              <div className="tp-slots__step">
                <div className="tp-slots__step-head">
                  <Badge tone="primary" solid>2</Badge>
                  <span className="tp-slots__step-title">Remove affected slots</span>
                  <span className="tp-slots__hint">
                    (click × on slots the teacher can't take that day)
                  </span>
                </div>

                {draftLeaveSlots.length === 0 ? (
                  <p className="tp-slots__inline-empty">
                    No weekly slots on this day — nothing to remove.
                  </p>
                ) : (
                  <div className="tp-slots__chips">
                    {draftLeaveSlots.map((slot) => (
                      <Chip
                        key={slot.id}
                        onRemove={() => removeDraftSlot(slot.id)}
                        removeLabel="Remove this slot from the leave day"
                      >
                        {toAmPm(slot.startTime)} – {toAmPm(slot.endTime)}
                      </Chip>
                    ))}
                  </div>
                )}
              </div>
              </>)} {/* end !draftFullDay — Step 2 only */}

              {/* Step 3: Queue more dates — always visible */}
              <div className="tp-slots__panel">
                <div className="tp-slots__bar-row tp-slots__bar-row--between">
                  <span className="tp-slots__muted">
                    Need to apply leave for <b>multiple dates</b>? Queue this date, then pick the next.
                  </span>
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={<Plus size={14} aria-hidden />}
                    onClick={addLeaveDateConfig}
                    disabled={!draftLeaveDate}
                  >
                    Queue this date
                  </Button>
                </div>

                {leaveConfigs.length > 0 && (
                  <div className="tp-slots__queue">
                    <div className="tp-slots__queue-title">
                      Queued ({leaveConfigs.length}):
                    </div>
                    {leaveConfigs.map((cfg) => {
                      const removedCount = (() => {
                        const dayKey = getDayKeyFromDate(cfg.date);
                        const source = slotsByDay[dayKey] || [];
                        return source.length - cfg.slots.length;
                      })();
                      return (
                        <div key={cfg.id} className="tp-slots__queue-row">
                          <span className="tp-slots__queue-date">
                            <b>{cfg.date}</b>
                            {removedCount > 0 && (
                              <Badge tone="accent">
                                {removedCount} slot{removedCount !== 1 ? "s" : ""} removed
                              </Badge>
                            )}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            aria-label={`Remove ${cfg.date} from the queue`}
                            title="Remove from queue"
                            icon={<X size={14} aria-hidden />}
                            onClick={() => removeLeaveDateConfig(cfg.id)}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </ModalBody>
            <ModalFooter className="tp-slots__footer-split">
              <span className="tp-slots__hint">
                {draftFullDay
                  ? `Marks ${draftLeaveDate || "selected date"} as full day unavailable.`
                  : `Applies leave for current date${leaveConfigs.length > 0 ? ` + ${leaveConfigs.length} queued` : ""}.`}
              </span>
              <div className="tp-slots__actions">
                <Button variant="secondary" size="sm" onClick={() => setIsLeaveModalOpen(false)}>
                  Cancel
                </Button>
                <Button variant="primary" size="sm" onClick={handleConfirmLeaveChanges}>
                  Block Calendar
                </Button>
              </div>
            </ModalFooter>
          </>
        )}

        {/* ── TAB B: View & Delete ───────────────────────────────── */}
        {leaveModalTab === "view" && (
          <>
            <ModalBody className="tp-slots__modal-scroll">
              {aeUnavailableSlots.length === 0 ? (
                <EmptyState
                  title="No unavailability records found for this teacher."
                  action={
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setLeaveModalTab("apply");
                      }}
                    >
                      Apply leave now
                    </Button>
                  }
                />
              ) : (
                <div className="tp-slots__stack">
                  {aeUnavailableSlots
                    .slice()
                    .sort((a, b) => a.date.localeCompare(b.date))
                    .map((group) => (
                      <div key={`view-${group.date}`} className="tp-slots__leave-group">
                        {/* Date header */}
                        <div className="tp-slots__leave-head">
                          <span className="tp-slots__leave-date">{group.date}</span>
                          {group.isFullDay && <Badge tone="warning">Full day</Badge>}
                          {(group.timings || []).length > 0 && (
                            <span className="tp-slots__hint">
                              {group.timings!.length} slot{group.timings!.length !== 1 ? "s" : ""}
                            </span>
                          )}
                        </div>

                        {/* Timings list — read-only chips */}
                        <div className="tp-slots__chips tp-slots__leave-body">
                          {(group.timings || []).length === 0 && group.isFullDay ? (
                            <span className="tp-slots__hint">Full day — no individual timings.</span>
                          ) : (group.timings || []).length === 0 ? (
                            <span className="tp-slots__hint">No timings recorded.</span>
                          ) : (
                            (group.timings || []).map((t) => (
                              <Chip key={`view-timing-${t.id}`} tone="danger">
                                {toAmPm(normalizeTemplateTime(t.startTime))} – {toAmPm(normalizeTemplateTime(t.endTime))}
                              </Chip>
                            ))
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </ModalBody>
            <ModalFooter>
              <Button variant="secondary" size="sm" onClick={() => setIsLeaveModalOpen(false)}>
                Close
              </Button>
            </ModalFooter>
          </>
        )}
      </Modal>

      {/* ── Delete slots confirmation modal ─────────────────── */}
      <Modal
        isOpen={deleteConfirm !== null}
        toggle={() => !deleteConfirmBusy && setDeleteConfirm(null)}
        size="md"
        backdrop={deleteConfirmBusy ? "static" : true}
        keyboard={!deleteConfirmBusy}
      >
        <ModalHeader toggle={() => !deleteConfirmBusy && setDeleteConfirm(null)}>
          Confirm slot deletion
        </ModalHeader>
        <ModalBody>
          {deleteConfirm && (
            <div className="tp-slots__stack">
              <p className="tp-slots__copy">
                You are about to remove the following{" "}
                <b>{deleteConfirm.timings.length} slot{deleteConfirm.timings.length !== 1 ? "s" : ""}</b>{" "}
                blocked on <b>{deleteConfirm.date}</b>:
              </p>
              <div className="tp-slots__chips">
                {deleteConfirm.timings.map((t) => (
                  <Chip key={`del-confirm-${t.id}`} tone="danger">
                    {toAmPm(normalizeTemplateTime(t.startTime))} – {toAmPm(normalizeTemplateTime(t.endTime))}
                  </Chip>
                ))}
              </div>
              <p className="tp-slots__hint">This action cannot be undone.</p>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            variant="secondary"
            size="sm"
            disabled={deleteConfirmBusy}
            onClick={() => setDeleteConfirm(null)}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            size="sm"
            disabled={deleteConfirmBusy}
            onClick={() => void confirmDeleteSlots()}
          >
            {deleteConfirmBusy ? "Deleting…" : "Yes, Delete"}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={clearConfirmTarget !== null}
        toggle={closeClearConfirm}
        size="md"
        backdrop={clearConfirmBusy ? "static" : true}
        keyboard={!clearConfirmBusy}
      >
        <ModalHeader toggle={closeClearConfirm} closeButton={!clearConfirmBusy}>
          {clearConfirmTarget === CLEAR_CONFIRM_ALL ? "Delete the slots" : "Confirm clear"}
        </ModalHeader>
        <ModalBody>
          {clearConfirmTarget === CLEAR_CONFIRM_ALL ? (
            <div className="tp-slots__stack">
              <p className="tp-slots__copy">
                Choose how to reset blueprint or delete the slots for this teacher:
              </p>
              <div className="tp-slots__options">
                <div className="tp-slots__option">
                  <Badge tone="danger">Delete blueprint and slots</Badge>
                  <span>Remove blueprint and teacher future free slots.</span>
                </div>
                <div className="tp-slots__option">
                  <Badge tone="warning">Delete blueprint only</Badge>
                  <span>Removes only the blueprint by keeping existing free and paid slots.</span>
                </div>
                <div className="tp-slots__option">
                  <Badge tone="info">Delete from desired date</Badge>
                  <span>Remove blueprint and deletes the slots from the date provided below.</span>
                </div>
              </div>
              <Field
                label="Delete slots from"
                hint="The weekly grid will be cleared locally after reset. You can add slots and publish again when needed."
              >
                {(control) => (
                  <TextInput
                    {...control}
                    type="date"
                    size="sm"
                    className="tp-slots__date"
                    min={getTodayDateString()}
                    value={resetDeleteFromDate}
                    onChange={(e) => handleFutureDateInput(e.target.value, setResetDeleteFromDate)}
                    disabled={clearConfirmBusy}
                  />
                )}
              </Field>
            </div>
          ) : clearConfirmTarget ? (
            <div className="tp-slots__stack">
              <p className="tp-slots__copy">
                Clear all slots for <strong>{getDayLabel(clearConfirmTarget)}</strong>?
              </p>
              <p className="tp-slots__hint">
                {hasPersistedServerTemplate
                  ? "This updates the server immediately (that day is saved with no time slots)."
                  : "This only clears that day in the editor. Use Save schedule to sync to the server."}
              </p>
            </div>
          ) : null}
        </ModalBody>
        <ModalFooter
          className={clearConfirmTarget === CLEAR_CONFIRM_ALL ? "tp-slots__footer-stack" : undefined}
        >
          {clearConfirmTarget === CLEAR_CONFIRM_ALL ? (
            <>
              <div className="tp-slots__option-actions">
                <Button
                  variant="danger"
                  size="sm"
                  disabled={clearConfirmBusy}
                  onClick={() => void confirmClearAll("default")}
                >
                  {clearConfirmBusy ? "Please wait..." : "Delete blueprint and slots"}
                </Button>
                <Button
                  variant="accent"
                  size="sm"
                  disabled={clearConfirmBusy}
                  onClick={() => void confirmClearAll("templatesOnly")}
                >
                  {clearConfirmBusy ? "Please wait..." : "Delete blueprint only"}
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  disabled={
                    clearConfirmBusy ||
                    !resetDeleteFromDate.trim() ||
                    isPastDateString(resetDeleteFromDate)
                  }
                  onClick={() => void confirmClearAll("fromDate")}
                >
                  {clearConfirmBusy ? "Please wait..." : "Delete from desired date"}
                </Button>
              </div>
              <div className="tp-slots__actions tp-slots__actions--end">
                <Button variant="secondary" size="sm" disabled={clearConfirmBusy} onClick={closeClearConfirm}>
                  Cancel
                </Button>
              </div>
            </>
          ) : (
            <>
              <Button variant="secondary" size="sm" disabled={clearConfirmBusy} onClick={closeClearConfirm}>
                Cancel
              </Button>
              <Button variant="danger" size="sm" disabled={clearConfirmBusy} onClick={() => void confirmClear()}>
                {clearConfirmBusy ? "Please wait..." : "Clear"}
              </Button>
            </>
          )}
        </ModalFooter>
      </Modal>

      {DAYS.map((day) => (
        <Card
          key={day.key}
          className="tp-slots__day"
          title={day.label}
          actions={
            <div className="tp-slots__day-actions">
              <Button
                variant="secondary"
                size="sm"
                icon={<Plus size={14} aria-hidden />}
                aria-expanded={expandedAddSlotDayKey === day.key}
                disabled={scheduleLoading || saving}
                onClick={() => toggleExpandedAddSlot(day.key)}
              >
                Add slot
              </Button>
              <DsSelect
                size="sm"
                aria-label={`Copy slots to ${day.label} from another day`}
                className="tp-slots__copy-from"
                disabled={copyFromGloballyDisabled}
                title={
                  totalSlots === 0
                    ? "Add slots on at least one day before you can copy from another day."
                    : undefined
                }
                value={copyFromByDay[day.key] || ""}
                onChange={(e) => {
                  const selectedFromDay = e.target.value;
                  if (selectedFromDay) {
                    copyDaySlots(selectedFromDay, day.key);
                  }
                  setCopyFromByDay((prev) => {
                    const next = { ...prev };
                    delete next[day.key];
                    return next;
                  });
                }}
                options={[
                  { value: "", label: "Copy from" },
                  ...DAYS.filter((d) => d.key !== day.key && (slotsByDay[d.key] || []).length > 0).map((d) => ({
                    value: d.key,
                    label: `${d.short} (${(slotsByDay[d.key] || []).length})`,
                  })),
                ]}
              />
              <Button
                variant="ghost"
                size="sm"
                className="tp-slots__danger-ghost"
                disabled={
                  (slotsByDay[day.key] || []).length === 0 ||
                  clearingDayKey === day.key ||
                  scheduleLoading ||
                  saving ||
                  clearingSchedule
                }
                onClick={() => openClearDayConfirm(day.key)}
              >
                {clearingDayKey === day.key ? "Clearing..." : "Clear"}
              </Button>
            </div>
          }
        >
          <Collapse isOpen={expandedAddSlotDayKey === day.key}>
            <div className="tp-slots__add-panel">
              <p className="tp-slots__add-help">
                <strong>{day.label}</strong>
                {" · "}
                {slotSplitStepMinutes === 30 ? (
                  <>
                    Range becomes consecutive <strong>30-minute</strong> slots (e.g. 1–2 PM → 1:00–1:30 and
                    1:30–2:00).
                  </>
                ) : (
                  <>
                    Range becomes consecutive <strong>1-hour</strong> slots (e.g. 1–6 PM → five hourly blocks).
                  </>
                )}
              </p>
              <div className="tp-slots__add-row">
                <Field label="Start" className="tp-slots__time">
                  {(control) => (
                    <DsSelect
                      {...control}
                      size="sm"
                      value={startTime}
                      onChange={(e) => handleStartTimeChange(e.target.value)}
                      options={timeOptions}
                    />
                  )}
                </Field>
                <Field label="End" className="tp-slots__time">
                  {(control) => (
                    <DsSelect
                      {...control}
                      size="sm"
                      value={endTime}
                      onChange={(e) => setEndTime(e.target.value)}
                      options={timeOptions}
                    />
                  )}
                </Field>

                <Button variant="accent" size="sm" onClick={() => submitAddSlotForDay(day.key)}>
                  Add
                </Button>
                {/* <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    marginLeft: "auto",
                    flexWrap: "wrap",
                  }}
                >
                  <Button
                    color="secondary"
                    size="sm"
                    outline
                    style={{ height: 32, fontSize: 12, borderRadius: 8 }}
                    onClick={closeExpandedAddSlotPanel}
                  >
                    Cancel
                  </Button>

                </div> */}
              </div>
            </div>
          </Collapse>
          {(slotsByDay[day.key] || []).length > 0 && (
            <div className="tp-slots__chips">
              {(slotsByDay[day.key] || []).map((slot) => (
                <Chip
                  key={slot.id}
                  onRemove={() => deleteDaySlot(day.key, slot.id)}
                  removeLabel={`Remove ${toAmPm(slot.startTime)} - ${toAmPm(slot.endTime)}`}
                >
                  {toAmPm(slot.startTime)} - {toAmPm(slot.endTime)}
                </Chip>
              ))}
            </div>
          )}
        </Card>
      ))}
      </div>

      <div className="tp-slots__footer">
        <span className="tp-slots__hint">{totalSlots} slots across 7 days</span>
        <div className="tp-slots__actions">
          <Button
            variant="ghost"
            size="sm"
            className="tp-slots__danger-ghost"
            onClick={openClearAllConfirm}
            disabled={clearingSchedule || scheduleLoading || saving}
          >
            {clearingSchedule ? "Deleting..." : "Delete the slots"}
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              if (!canPublishSchedule) return;
              setPublishConfirmOpen(true);
            }}
            disabled={!canPublishSchedule}
            title={
              totalSlots === 0
                ? "Add at least one slot to insert"
                : !hasScheduleChanges
                  ? "No changes to insert"
                  : needsOfflineCentre
                    ? "Select an offline centre to insert slots"
                    : undefined
            }
          >
            {saving ? "Saving..." : "Add Slot Insertion"}
          </Button>
        </div>
      </div>
      {/* ── Publish confirmation modal ───────────────────────── */}
      <Modal
        isOpen={publishConfirmOpen}
        toggle={() => !saving && setPublishConfirmOpen(false)}
        size="lg"
        backdrop={saving ? "static" : true}
        keyboard={!saving}
      >
        <ModalHeader toggle={() => !saving && setPublishConfirmOpen(false)}>
          Confirm to Insert Slots
        </ModalHeader>
        <ModalBody className="tp-slots__stack">
          <div>
            <p className="tp-slots__copy">
              You are about to insert <b>{totalSlots} slot{totalSlots !== 1 ? "s" : ""}</b> for {teacherProfileDetails.firstName} {teacherProfileDetails.lastName} teacher.
            </p>
            <p className="tp-slots__hint">
              {hasPersistedServerTemplate
                ? "Choose how this schedule update should take effect."
                : "This is a new schedule. Please select the effective date from which it should take effect."}
            </p>
          </div>

          {!hasPersistedServerTemplate && (
            <Field
              label="Effective Date from"
              required
              error={
                !templateForm.effectiveFrom
                  ? "Please select a date to continue."
                  : isPastDateString(templateForm.effectiveFrom)
                    ? "Past dates are not allowed."
                    : undefined
              }
            >
              {(control) => (
                <TextInput
                  {...control}
                  type="date"
                  size="sm"
                  className="tp-slots__date"
                  min={getTodayDateString()}
                  value={templateForm.effectiveFrom}
                  onChange={(e) =>
                    handleFutureDateInput(e.target.value, (next) =>
                      setTemplateForm((prev) => ({ ...prev, effectiveFrom: next }))
                    )
                  }
                />
              )}
            </Field>
          )}
        </ModalBody>
        <ModalFooter className="tp-slots__footer-end">
          {hasPersistedServerTemplate ? (
            <>
              <Button
                variant="primary"
                size="sm"
                disabled={saving}
                onClick={() => {
                  void saveSchedule({ applyImmediately: true });
                  setPublishConfirmOpen(false);
                }}
              >
                {saving ? "Saving…" : "Apply Now"}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={saving}
                onClick={() => {
                  void saveSchedule();
                  setPublishConfirmOpen(false);
                }}
              >
                {saving ? "Saving…" : "Apply from Next Month"}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={saving}
                onClick={() => {
                  setPublishEffectiveFrom(getTodayDateString());
                  setPublishConfirmOpen(false);
                  setPublishDateModalOpen(true);
                }}
              >
                Choose Effective Date
              </Button>
            </>
          ) : (
            <Button
              variant="primary"
              size="sm"
              disabled={
                saving ||
                !templateForm.effectiveFrom ||
                isPastDateString(templateForm.effectiveFrom)
              }
              onClick={() => {
                void saveSchedule();
                setPublishConfirmOpen(false);
              }}
            >
              {saving ? "Saving…" : "Confirm to Insert Slots"}
            </Button>
          )}
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={publishDateModalOpen}
        toggle={() => !saving && setPublishDateModalOpen(false)}
        size="sm"
        backdrop={saving ? "static" : true}
        keyboard={!saving}
      >
        <ModalHeader toggle={() => !saving && setPublishDateModalOpen(false)}>
          Choose Effective Date
        </ModalHeader>
        <ModalBody className="tp-slots__stack">
          <p className="tp-slots__copy">
            Select the effective date from which this schedule should take effect.
          </p>
          <Field
            label="Effective Date from"
            required
            error={
              !publishEffectiveFrom
                ? "Please select a date to continue."
                : isPastDateString(publishEffectiveFrom)
                  ? "Past dates are not allowed."
                  : undefined
            }
          >
            {(control) => (
              <TextInput
                {...control}
                type="date"
                size="sm"
                className="tp-slots__date"
                min={getTodayDateString()}
                value={publishEffectiveFrom}
                onChange={(e) => handleFutureDateInput(e.target.value, setPublishEffectiveFrom)}
              />
            )}
          </Field>
        </ModalBody>
        <ModalFooter className="tp-slots__footer-end">
          <Button
            variant="secondary"
            size="sm"
            disabled={saving}
            onClick={() => setPublishDateModalOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={
              saving ||
              !publishEffectiveFrom ||
              isPastDateString(publishEffectiveFrom)
            }
            onClick={() => {
              void saveSchedule({ effectiveFrom: publishEffectiveFrom, applyImmediately: true });
              setPublishDateModalOpen(false);
            }}
          >
            {saving ? "Saving…" : "Confirm to Insert Slots"}
          </Button>
        </ModalFooter>
      </Modal>

    </div>
  );
};

export default Slots;
